/**
 * tokenUtils - Sichere Auswahl des Kunden-Tokens für Portal-API-Calls.
 *
 * Hintergrund (Security-Hardening 06/2026): Mehrere Portal-Komponenten haben
 * bisher blind `localStorage.getItem('customerToken') || localStorage.getItem('token')`
 * genutzt. Das Feld `token` kann aber einen Admin-/Trainer-/Staff-Token
 * enthalten (Admin ist im selben Browser eingeloggt). Diese naive Fallback-
 * Kette konnte also einen Staff-Token an Portal-Endpoints senden.
 *
 * Dieser Helfer dekodiert das JWT-Payload NUR zum Lesen der Claims (KEINE
 * Signaturprüfung — die macht das Backend) und akzeptiert einen Token für das
 * Kundenportal ausschliesslich dann, wenn er zu einem Kunden gehört.
 *
 * Token-Claim-Formen (Backend):
 *   - Portal-Login   (create_customer_token): { type:"customer", role:"customer" }
 *   - Refresh/Std.   (create_access_token):   { role:"customer" }  (kein type)
 *   - Staff/Admin    (create_access_token):   { role: admin|trainer|... }
 */

const STAFF_ROLES = new Set(['admin', 'superadmin', 'trainer', 'staff', 'mieter']);

const isUsable = (v) => !!v && v !== 'undefined' && v !== 'null';

/**
 * Dekodiert das Payload eines JWT (base64url) ohne Signaturprüfung.
 * Gibt bei ungültigem/fehlerhaftem Token `null` zurück.
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const decoded = atob(b64);
    const json = decodeURIComponent(
      decoded
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

/** true, wenn der Token einem Staff-/Admin-/Trainer-/Mieter-Konto gehört. */
export function isStaffToken(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  const role = String(payload.role || '').toLowerCase();
  return STAFF_ROLES.has(role);
}

/** true, wenn der Token nachweislich zu einem Kunden gehört (und NICHT Staff). */
export function isCustomerToken(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  const role = String(payload.role || '').toLowerCase();
  const type = String(payload.type || '').toLowerCase();
  if (STAFF_ROLES.has(role)) return false;
  return role === 'customer' || type === 'customer';
}

/**
 * Liefert den für Portal-Calls zu verwendenden Token.
 *
 * Reihenfolge:
 *   1. Primär-Slots (Default: 'customerToken') — akzeptiert, solange KEIN
 *      Staff-Token darin liegt. (Nur der Portal-Flow schreibt diese Slots.)
 *   2. Legacy-Slot 'token' — NUR akzeptiert, wenn es ein Kunden-Token ist
 *      (schliesst geleakte Admin-/Trainer-Tokens aus).
 *
 * @param {string[]} primaryKeys localStorage-Schlüssel, die zuerst geprüft werden.
 * @returns {string|null}
 */
export function getPortalToken(primaryKeys = ['customerToken']) {
  for (const key of primaryKeys) {
    const v = localStorage.getItem(key);
    if (isUsable(v) && !isStaffToken(v)) return v;
  }
  const legacy = localStorage.getItem('token');
  if (isUsable(legacy) && isCustomerToken(legacy)) return legacy;
  return null;
}

/**
 * Authorization-Header-Wert für ÖFFENTLICHE Portal-Calls
 * (z.B. /portal/settings/public, /portal/auth/google/config).
 *
 * Gibt `Bearer <kundentoken>` zurück, falls vorhanden — sonst `null`. Ein
 * `null`-Wert weist axios (v1) an, den global gesetzten Authorization-Default
 * (axios.defaults, kann bei parallel eingeloggtem Admin ein Staff-Token sein)
 * für DIESEN Request zu entfernen. So wird nie ein geleakter Admin-Token an
 * einen öffentlichen Endpunkt gesendet (Defense-in-Depth).
 */
export function portalAuthHeaderValue() {
  const t = getPortalToken();
  return t ? `Bearer ${t}` : null;
}
